import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from '@/utils/logger';

/**
 * Hook to observe email link clicks for analytics and clean tracking params
 * out of the URL.
 *
 * It deliberately does NOT navigate. All email deep links route through
 * `/start` (StartQuizRedirect), which is the single source of truth for
 * rebuilding quiz intent and handling the logged-out sign-in round-trip via
 * the `from` param that SignIn/PrivateRoute actually honor. This hook used to
 * fan out to per-source targets (`/quiz/continue`, `/results`, `/practice`)
 * that 404 today and competed with `/start` — that switch has been removed so
 * there is exactly one deep-link engine.
 */
export const useEmailLinkHandler = () => {
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    // Accept the legacy email_source/email_campaign params as well as the
    // utm_* params the "letters from Pam" actually emit. Only EMAIL traffic
    // counts: a bare utm_source fallback here fired email_link_click for every
    // UTM-tagged arrival (Google Ads clicks included), polluting email analytics.
    const isEmailTraffic =
      Boolean(urlParams.get('email_source')) ||
      urlParams.get('utm_source') === 'email' ||
      urlParams.get('utm_medium') === 'email';
    if (!isEmailTraffic) return;

    const emailSource = urlParams.get('email_source') || urlParams.get('utm_source');
    const emailCampaign = urlParams.get('email_campaign') || urlParams.get('utm_campaign');

    logger.info('EmailLinkHandler', 'Observed email link', {
      source: emailSource,
      campaign: emailCampaign,
      path: location.pathname,
    });

    // Track email link clicks for analytics (observe only — no navigation).
    if (typeof gtag !== 'undefined') {
      gtag('event', 'email_link_click', {
        email_source: emailSource,
        email_campaign: emailCampaign,
        page_path: location.pathname,
      });
    }

    // Clean up tracking params so they don't leak into shared/bookmarked URLs.
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('email_source');
    cleanUrl.searchParams.delete('email_campaign');
    window.history.replaceState({}, '', cleanUrl.toString());
  }, [location.search, location.pathname]);
};

/**
 * Helper function to generate email tracking URLs
 */
export const generateEmailTrackingUrl = (
  baseUrl: string,
  emailSource: string,
  emailCampaign?: string,
  userId?: string
): string => {
  const url = new URL(baseUrl, 'https://socialworktestprepacademy.com');
  
  url.searchParams.set('email_source', emailSource);
  if (emailCampaign) {
    url.searchParams.set('email_campaign', emailCampaign);
  }
  if (userId) {
    url.searchParams.set('user_id', userId);
  }
  
  return url.toString();
};